import { useTranslation } from 'react-i18next'
import { Alpha3Code } from 'i18n-iso-countries'

import { SearchAndFilters } from '../searchAndFilters/SearchAndFilters'
import { useCandidates } from '../candidatesContext/useCandidatesContext'
import { formatDate, getMatchedScoreDisplay, mapTaskToAssigneePickerStatus } from '../../utils/utils'
import styles from '../CandidatesContainer.module.scss'
import { useTaskAssignees } from '../../hooks/useTaskAssignees'
import { isScoreFilterApplied } from '../searchAndFilters/hooks/useScoreFilter'

import {
  FlagAvatar,
  Flex,
  Loader,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TablePagination,
  TableProvider,
  TableRow,
  Tooltip,
  AssigneePicker
} from '@percent/lemonade'
import { getCountryNameFromAlpha3Code } from '@percent/partner-dashboard/i18n/countries'
import { Task, TaskTemplate } from '@percent/partner-dashboard/services/grants/grantsService.types'

type TaskCandidatesTableProps = {
  taskTemplates?: TaskTemplate[]
  scoreName?: string | null
}

export function TaskCandidatesTable({ taskTemplates = [], scoreName }: TaskCandidatesTableProps) {
  const { t } = useTranslation()
  const { candidates, sort, navigateToCandidate, queryParams } = useCandidates()
  const {
    loadingTaskAssignees,
    isUpdatingTaskAssignee,
    canAssignTasks,
    handleAssigneeChange,
    getFilteredAssignees,
    getAssignee,
    getSearchProps,
    getTooltipContent
  } = useTaskAssignees()

  const isSearchActive = !!queryParams.searchTerm && queryParams.searchTerm.trim() !== ''
  const assigneeFilterApplied = (queryParams.assigneeEmails?.length ?? 0) > 0 || Boolean(queryParams.includeUnassigned)
  const filtersApplied = isSearchActive || assigneeFilterApplied || isScoreFilterApplied(queryParams)

  const hasScoreColumn = !!scoreName
  const hasSubmittedAtColumn = taskTemplates.length === 0

  const columnsHeaders: TableColumn[] = [
    { id: 'country', content: t('table.header.country'), isSortable: false, props: { width: '10%' } },
    {
      id: 'organization',
      content: t('table.header.organization'),
      isSortable: false,
      props: { width: taskTemplates.length > 0 ? '60%' : '75%' }
    },
    ...(hasScoreColumn ? [{ id: 'score', content: scoreName ?? '', isSortable: true, props: { width: '15%' } }] : []),
    ...(hasSubmittedAtColumn
      ? [{ id: 'submittedAt', content: t('table.header.submittedAt'), isSortable: true, props: { width: '15%' } }]
      : []),
    ...taskTemplates.map(taskTemplate => ({
      id: `task-${taskTemplate.id}`,
      content: taskTemplate.name,
      isSortable: false,
      props: { width: 'auto' }
    }))
  ]

  if (candidates.isLoading || loadingTaskAssignees) {
    return <Loader />
  }

  const emptyTableTitle = filtersApplied
    ? t('grants.searchEmptyTable.title')
    : t('grants.candidatesInStateTable.emptyTable.title')
  const emptyTableDescription = filtersApplied ? '' : t('grants.candidatesInStateTable.emptyTable.description')
  const illustration = filtersApplied ? 'no-results' : 'no-grant-candidates'

  return (
    <TableProvider
      columns={columnsHeaders}
      totalResults={candidates.totalResults}
      isLoading={candidates.isLoading}
      handleRequestSort={columnKey =>
        sort.handleRequestSort(columnKey, columnKey === 'score' ? { scoreName: scoreName ?? undefined } : undefined)
      }
      sortDirection={sort.direction}
      sortByColumn={queryParams.sortBy ?? 'submittedAt'}
      illustration={illustration}
      emptyTableTitle={emptyTableTitle}
      emptyTableDescription={emptyTableDescription}
      className={styles.tableWrapper}
    >
      {(candidates.totalResults > 0 || filtersApplied) && (
        <Flex direction="column" data-testid="search-and-filters">
          <SearchAndFilters
            enabledFilters={{ organisationSearch: true, currentState: false, assignee: true, score: hasScoreColumn }}
            scoreName={scoreName}
          />
        </Flex>
      )}
      <Table>
        <TableBody>
          {candidates.data?.map(({ id, organisation, createdAt, tasks, scores }) => {
            const scoreDisplay = hasScoreColumn ? getMatchedScoreDisplay(scores, scoreName) : null

            return (
              <TableRow
                key={`candidate-${id}`}
                data-testid={`candidate-${id}`}
                onClick={() => navigateToCandidate(id)}
                className={styles.tableRow}
              >
                <TableCell inlineStyle={{ width: '150px' }}>
                  <Flex align="center">
                    <Tooltip
                      content={getCountryNameFromAlpha3Code(organisation.countryCode as Alpha3Code)}
                      placement="right"
                    >
                      <div className={styles.tooltipContent}>
                        <FlagAvatar code3={organisation.countryCode as Alpha3Code} width="28px" />
                      </div>
                    </Tooltip>
                  </Flex>
                </TableCell>
                <TableCell>{organisation.name}</TableCell>

                {hasScoreColumn && <TableCell data-testid={`candidate-${id}-score`}>{scoreDisplay}</TableCell>}
                {hasSubmittedAtColumn && <TableCell>{formatDate(createdAt)}</TableCell>}

                {taskTemplates.map(taskTemplate => {
                  const task = tasks?.find(task_ => task_.templateId === taskTemplate.id)

                  if (!task) {
                    return <TableCell key={`task-${taskTemplate.id}`} />
                  }

                  const filteredAssignees = getFilteredAssignees(taskTemplate.id)

                  const { status, label } = mapTaskToAssigneePickerStatus(
                    { status: task.status, dueByDate: task.deadline } as Task,
                    t
                  )

                  const assignee = getAssignee(task.id, task.assignee)

                  return (
                    <TableCell key={`task-${taskTemplate.id}`} onClick={e => e.stopPropagation()}>
                      <AssigneePicker
                        status={status}
                        statusLabel={label}
                        assignee={assignee}
                        assigneeList={filteredAssignees}
                        onAssigneeChange={assignee_ => handleAssigneeChange(task.id, assignee_)}
                        search={getSearchProps(taskTemplate.id)}
                        isLoading={loadingTaskAssignees || isUpdatingTaskAssignee}
                        triggerTestId={`assignee-picker-${id}-${taskTemplate.id}`}
                        tooltipContent={getTooltipContent(task, assignee)}
                        disabled={status === 'completed' || !canAssignTasks || !task.id}
                      />
                    </TableCell>
                  )
                })}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      <Flex direction="column" p="10px">
        <TablePagination
          totalResultsText={t('table.results', { count: candidates.totalResults ?? undefined })}
          previousPage={candidates.previousPage}
          nextPage={candidates.nextPage}
        />
      </Flex>
    </TableProvider>
  )
}
